import { useMutation, useQuery } from "@tanstack/react-query"
import { useState } from "react"
import { useSearchParams } from "react-router-dom"
import GameScreenLayout from "@/components/GameScreenLayout"
import GeoPointsList from "@/components/geo/GeoPointsList"
import { normalizeGeoPoint } from "@/components/geo/geoPointHelpers"
import { usePlayerGpsState } from "@/hooks/usePlayerGpsState"
import {
  checkInHotel,
  checkOutHotel,
  getHotelPoints,
  getHotelStatus,
} from "@/api/gameApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"
import { HotelPackageSlug } from "@/types/hotel"

export default function HotelPage() {
  const [params] = useSearchParams()
  const highlightId = params.get("pointId")
  const gps = usePlayerGpsState()
  const { syncGameState } = useGuardiansPassport()
  const [packageSlug, setPackageSlug] = useState<HotelPackageSlug>("standard")

  const pointsQ = useQuery({
    queryKey: ["hotel-points"],
    queryFn: getHotelPoints,
  })

  const statusQ = useQuery({
    queryKey: ["hotel-status"],
    queryFn: getHotelStatus,
  })

  const packages = statusQ.data?.packages ?? []

  const checkIn = useMutation({
    mutationFn: (pointId: string) => {
      if (!gps) throw new Error("GPS")
      return checkInHotel({
        pointId,
        playerLat: gps.lat,
        playerLng: gps.lng,
        packageSlug,
      })
    },
    onSuccess: async () => {
      await syncGameState()
      void statusQ.refetch()
    },
  })

  const checkOut = useMutation({
    mutationFn: () => checkOutHotel(),
    onSuccess: async () => {
      await syncGameState()
      void statusQ.refetch()
    },
  })

  const points = (pointsQ.data ?? []).map((p) =>
    normalizeGeoPoint({
      id: p.id,
      name: p.name,
      latitude: p.latitude,
      longitude: p.longitude,
      radiusMeters: p.radiusMeters,
    })
  )

  return (
    <GameScreenLayout title="Гостиница" backTo="/play/map">
      {statusQ.data?.isResting ? (
        <div className="mb-4 space-y-2">
          <p className="text-sm text-guardians-accent">
            Отдых до{" "}
            {statusQ.data.restEndsAt
              ? new Date(statusQ.data.restEndsAt).toLocaleString("ru-RU")
              : "—"}
          </p>
          <button
            type="button"
            onClick={() => checkOut.mutate()}
            disabled={!statusQ.data.canCheckOut || checkOut.isPending}
            className="rounded-xl border border-white/20 px-4 py-2 text-sm disabled:opacity-40"
          >
            Выселиться
          </button>
        </div>
      ) : (
        <div className="mb-4 space-y-2">
          <p className="text-xs text-guardians-muted">Номер</p>
          <select
            value={packageSlug}
            onChange={(e) => setPackageSlug(e.target.value as HotelPackageSlug)}
            className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm"
          >
            {(packages.length
              ? packages
              : [
                  { slug: "coffee" as const, title: "Кофе" },
                  { slug: "standard" as const, title: "Стандарт" },
                  { slug: "full" as const, title: "Полный" },
                ]
            ).map((pkg) => (
              <option key={pkg.slug} value={pkg.slug}>
                {"title" in pkg && pkg.title
                  ? `${pkg.title} (${pkg.slug})`
                  : pkg.slug}
                {"priceCoins" in pkg && pkg.priceCoins != null
                  ? ` · ${pkg.priceCoins} монет`
                  : ""}
              </option>
            ))}
          </select>
        </div>
      )}
      <GeoPointsList
        points={points}
        gps={gps}
        highlightId={highlightId}
        renderActions={({ point, inRange }) => (
          <button
            type="button"
            disabled={
              !inRange || checkIn.isPending || statusQ.data?.isResting
            }
            onClick={() => checkIn.mutate(point.id)}
            className="rounded-lg bg-guardians-accent px-3 py-1.5 text-xs text-guardians-bg disabled:opacity-40"
          >
            Заселиться
          </button>
        )}
      />
      {(checkIn.isError || checkOut.isError) && (
        <p className="mt-3 text-sm text-red-300">
          {getGameApiErrorMessage(checkIn.error ?? checkOut.error, "Ошибка")}
        </p>
      )}
    </GameScreenLayout>
  )
}
